<template>
  <div class="contact-technical-sale">
    <adn-button
      kind="primary"
      class="contact-technical-sale__contact-button"
      data-test-contact-technician-btn
      @click="showTechnicalSaleContacts"
    >
      {{ buttonText }}
    </adn-button>
    <p-info-request-to-technical-sale-modal
      ref="contactTCModal"
      :technical-sales="technicalSales"
      :product-info="productInfoToRequest"
      data-test="contact-technical-sale-modal"
    ></p-info-request-to-technical-sale-modal>
  </div>
</template>

<script lang="ts">
import { useProductCard } from '@b2ag/catalog/src/useProductCard'
import { useProductCardMapper } from '@b2ag/catalog/src/useProductCard'
import { useUser } from '@b2ag/user/src/useUser'
import { measureUnit } from '@b2ag/l10n'
import { useAnalyticService } from '@b2ag/plugin-aladin-gtm'
import { getProductDefaultImageFull } from '@b2ag/product/src/services/products-images.constants'
import type { PropType } from 'vue'
import { computed, defineComponent, ref } from 'vue'
import PInfoRequestToTechnicalSaleModal from '../PInfoRequestToTechnicalSaleModal/PInfoRequestToTechnicalSaleModal.vue'
import AdnButton from '@b2ag/design-system/src/components/AdnButton/AdnButton.vue'
import { i18n } from '@b2ag/locale'

type ProductRequestInfo = { id: string; image: string; name: string; brand: string; units: string[] }

export default defineComponent({
  name: 'PProductInfoRequestBtn',
  components: { PInfoRequestToTechnicalSaleModal, AdnButton },
  props: {
    buttonText: {
      type: String,
      default: () => i18n._('Demander le prix'),
    },
    product: {
      type: Object as PropType<{ _id: string }>,
      default: () => null,
    },
  },
  setup(props) {
    const { profile } = useUser()
    const contactTCModal = ref<
      (typeof PInfoRequestToTechnicalSaleModal & { show: () => void; hide: () => void }) | null
    >(null)
    const technicalSales = computed(() => {
      return profile.value && (profile.value.technicalSales || profile.value.technical_sales)
    })
    let { product } = useProductCard(props.product._id)

    if (!product.value) {
      product = useProductCardMapper(ref(props.product)).product
    }

    const defaultImage = computed(() =>
      product.value ? getProductDefaultImageFull(product.value.kind, product.value.type) : '',
    )
    const units = computed(() => {
      const unitsSet = new Set<string>()
      if (product.value && product.value.variants) {
        product.value.variants.forEach((variant) => {
          unitsSet.add(measureUnit(variant.measure_unit))
        })
      }
      return Array.from(unitsSet)
    })

    const productInfoToRequest = computed<ProductRequestInfo>(() => {
      return {
        id: product.value?._id ?? '',
        image: product.value?.thumbnail || (product.value?.images && product.value?.images[0]) || defaultImage.value,
        name: product.value?.name || '',
        brand: product.value?.supplier || product.value?.provider || 'NC',
        units: units.value,
      }
    })

    function showTechnicalSaleContacts() {
      contactTCModal.value?.show()
      useAnalyticService().trackEvent('click_ask_price', {
        product_name: product.value?.name,
        supplier: product.value?.supplier,
        partner_id: profile.value?.cooperative?.id,
      })
    }

    return { contactTCModal, profile, productInfoToRequest, technicalSales, showTechnicalSaleContacts }
  },
})
</script>

<style lang="scss">
.contact-technical-sale {
  width: 100%;

  .contact-technical-sale__contact-button {
    width: 100%;

    @media (min-width: $breakpoint-sm) {
      max-width: 28.8rem;
      transform: translateX(50%);
    }

    @media (min-width: $breakpoint-md) {
      transform: translateX(0);
    }
  }
}
</style>
